"use client";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** Common props for both timeout types */
interface BaseProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Seconds remaining until session expires */
  secondsRemaining: number;
  /** Called when user clicks "Log Out" / "Login Again" */
  onLogout: () => void;
}

/** Props specific to idle timeout warning */
interface IdleTimeoutProps extends BaseProps {
  timeoutType: "idle";
  /** Called when user clicks "Stay Logged In" */
  onExtend: () => void;
  onDismiss?: never;
}

/** Props specific to absolute timeout warning */
interface AbsoluteTimeoutProps extends BaseProps {
  timeoutType: "absolute";
  /** Called when user dismisses the modal */
  onDismiss: () => void;
  onExtend?: never;
}

/** Props for the session-expired warning (refresh token dead) */
interface SessionExpiredProps extends BaseProps {
  timeoutType: "session_expired";
  /** Called when user dismisses the modal */
  onDismiss: () => void;
  onExtend?: never;
}

type SessionTimeoutWarningModalProps = IdleTimeoutProps | AbsoluteTimeoutProps | SessionExpiredProps;

/**
 * Formats seconds into a human-readable string (e.g., "2:30" or "30s")
 */
function formatTime(seconds: number): string {
  if (seconds <= 0) return "0s";

  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (minutes > 0) {
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }
  return `${secs}s`;
}

/**
 * Modal that warns users before their session times out.
 *
 * Shows a countdown timer and allows users to either:
 * - Extend their session (for idle timeout)
 * - Log out immediately
 *
 * Uses shadcn Dialog component per gail-ui guidelines.
 */
export default function SessionTimeoutWarningModal(
  props: SessionTimeoutWarningModalProps
) {
  const { open, secondsRemaining, timeoutType, onLogout, onDismiss, onExtend } = props;
  const { t } = useTranslation("common");

  const isIdle = timeoutType === "idle";
  const isSessionExpired = timeoutType === "session_expired";
  const isDismissable = timeoutType === "absolute" || timeoutType === "session_expired";
  const timeDisplay = formatTime(secondsRemaining);

  // For idle timeout: prevent closing via escape/click outside (must choose action)
  // For absolute/session_expired: allow dismissing
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen && isDismissable && (timeoutType === "absolute" || timeoutType === "session_expired")) {
      onDismiss();
    }
  }, [isDismissable, timeoutType, onDismiss]);

  const handleDismiss = useCallback(() => {
    if (timeoutType === "absolute" || timeoutType === "session_expired") {
      onDismiss();
    }
  }, [timeoutType, onDismiss]);

  let title: string;
  let description: string;

  if (isIdle) {
    title = t("sessionTimeout.idleTitle");
    description = t("sessionTimeout.idleDescription");
  } else if (isSessionExpired) {
    title = t("sessionTimeout.sessionExpiredTitle");
    description = t("sessionTimeout.sessionExpiredDescription");
  } else {
    title = t("sessionTimeout.absoluteTitle");
    description = t("sessionTimeout.absoluteDescription");
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal={true}>
      <DialogContent
        className="!max-w-md"
        onPointerDownOutside={(e) => isIdle && e.preventDefault()}
        onEscapeKeyDown={(e) => isIdle && e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${isSessionExpired ? "bg-red-100" : "bg-yellow-100"}`}>
              <Icon icon="clock" className={`h-5 w-5 ${isSessionExpired ? "text-red-600" : "text-yellow-600"}`} />
            </div>
            <DialogTitle className="text-lg">{title}</DialogTitle>
          </div>
          <DialogDescription className="mt-2">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex flex-col items-center justify-center rounded-lg bg-100 p-4">
            <span className="text-sm text-500 mb-1">
              {isSessionExpired
                ? t("sessionTimeout.autoLogoutIn")
                : t("sessionTimeout.timeRemaining")}
            </span>
            <span className="text-3xl font-bold text-700 tabular-nums">
              {timeDisplay}
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {timeoutType === "idle" ? (
            <>
              <Button type="secondary" onClick={onLogout}>
                {t("sessionTimeout.logout")}
              </Button>
              <Button type="primary" onClick={onExtend}>
                {t("sessionTimeout.stayLoggedIn")}
              </Button>
            </>
          ) : (
            <>
              <Button type="secondary" onClick={handleDismiss}>
                {isSessionExpired
                  ? t("sessionTimeout.dismiss")
                  : t("sessionTimeout.continueWorking")}
              </Button>
              <Button type="primary" onClick={onLogout}>
                {t("sessionTimeout.loginAgain")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
